import React from 'react';

const BOTTOM_THRESHOLD = 50;

const useScrollContent = () => {
  const [canScroll, setCanScroll] = React.useState(false);

  const getElement = (id: string): HTMLElement => document.querySelector(id);

  const getContentHeight = React.useCallback(() => {
    const header = getElement('#header');
    const footer = getElement('#footer');
    const screenHeight = window.innerHeight;
    return screenHeight - header.clientHeight - footer.clientHeight;
  }, []);

  const updateCanScroll = React.useCallback(() => {
    const container = getElement('.content');
    if (container) {
      const { clientHeight } = container;
      const screenHeight = window.innerHeight;
      const contentHeight = getContentHeight();
      const { scrollHeight, scrollTop } = document.documentElement;

      const hasOverflow = clientHeight > contentHeight;
      const notAtBottom = scrollTop + screenHeight < scrollHeight - BOTTOM_THRESHOLD;
      setCanScroll(hasOverflow && notAtBottom);
    }
  }, [getContentHeight]);

  const handleScroll = React.useCallback(() => {
    const contentHeight = getContentHeight();
    window.scrollBy({ top: contentHeight, behavior: 'smooth' });
  }, [getContentHeight]);

  React.useLayoutEffect(() => {
    updateCanScroll();
    const resizeObserver = new ResizeObserver(updateCanScroll);
    window.addEventListener('scroll', updateCanScroll);
    window.addEventListener('resize', updateCanScroll);

    const container = getElement('.content');
    if (container) {
      resizeObserver.observe(container);
    }

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('scroll', updateCanScroll);
      window.removeEventListener('resize', updateCanScroll);
    };
  }, [updateCanScroll]);

  return { canScroll, handleScroll };
};

export default useScrollContent;
